import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Load from '../../components/Load'
import PartidaItem, { PartidaItemCategoriaResumo } from '../../components/PartidaItem'
import { partidasPorAtleta, Partida } from '../../services/partidaService'
import Header from '../../components/Header';
import { listarPagamentosPorCategoriaTorneio } from '../../services/pagamentoInscricaoService'

import { buscarEquipesPorCategoriaTorneio } from '../../services/equipeService'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Button from '@mui/material/Button'
import { categoriaTorneioPorAtleta, CategoriaTorneio } from '../../services/categoriaTorneioService'
import AtletaMatchesModal, { BasicAtletaInfo } from '../../components/AtletaMatchesModal'
import { atletasDoLado, atletaEstaNoLado } from '../../utils/atletasDoLado'

const AthleteMatchesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  if (!id) {
    navigate('/home');
  }
  const userStr = typeof window !== 'undefined' ? localStorage.getItem('user') : null
  let atletaInfo: BasicAtletaInfo = { id: id || '', nome: '' }
  try {
    const u = userStr ? JSON.parse(userStr) : null
    if (u) {
      atletaInfo = {
        id: id || '',
        nome: u.nome || '',
        username: u.username || null,
        imagemUrl: u.imagemUrl || null,
      }
    }
  } catch { }
  const [matches, setMatches] = useState<Partida[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [loadingInscricao, setLoadingInscricao] = useState(false)
  const [errorInscricao, setErrorInscricao] = useState('')
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null)
  const [trailMatches, setTrailMatches] = useState<Partida[]>([])
  const [partner, setPartner] = useState<BasicAtletaInfo | null>(null)
  const [trailOpen, setTrailOpen] = useState(false)
  const [categoriasRows, setCategoriasRows] = useState<PartidaItemCategoriaResumo[]>([])
  const [loadingCategorias, setLoadingCategorias] = useState(false)
  const [errorCategorias, setErrorCategorias] = useState('')
  const [selectedCategoryInfo, setSelectedCategoryInfo] = useState<{
    torneioId: string
    categoriaId: string
    categoriaNome: string
    torneioAvatarUrl: string | null
  }>({
    torneioId: '',
    categoriaId: '',
    categoriaNome: '',
    torneioAvatarUrl: null,
  })

  const [filtroTorneioId, setFiltroTorneioId] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroDataInicio, setFiltroDataInicio] = useState('')
  const [filtroDataFim, setFiltroDataFim] = useState('')

  useEffect(() => {
    if (!id) return
    setLoading(true)
    setLoadingCategorias(true)
    setErrorCategorias('')
    
    ;(async () => {
      try {
        // Carrega partidas e categorias em paralelo
        const [partidasData, confirmadas, pendentes] = await Promise.all([
          partidasPorAtleta(id).catch(() => []),
          categoriaTorneioPorAtleta(id, 'CONFIRMADA'),
          categoriaTorneioPorAtleta(id, 'PENDENTE'),
        ])
        
        setMatches(Array.isArray(partidasData) ? partidasData : [])
        
        const map = new Map<string, CategoriaTorneio>()
        for (const c of [...confirmadas, ...pendentes]) map.set(c.id, c)
        const cats = Array.from(map.values())
        const equipesCounts = await Promise.all(
          cats.map((c) => buscarEquipesPorCategoriaTorneio(c.id).then(arr => arr.length).catch(() => 0))
        )
        
        // Filtrar e ordenar partidas por categoria
        const rows: PartidaItemCategoriaResumo[] = cats.map((c, idx) => {
          // Filtrar partidas da categoria do estado matches
          const partidasDaCategoria = partidasData.filter(m => m.categoriaTorneio.id === c.id)
          
          // Filtrar partidas onde o atleta participa (equipe ou participante avulso — rodízio)
          const minhasPartidas = partidasDaCategoria.filter(p =>
            atletaEstaNoLado(p, 'A', id) || atletaEstaNoLado(p, 'B', id)
          )
          
          // Ordenar partidas por data (mais antigas primeiro)
          const partidasOrdenadas = [...minhasPartidas].sort((a, b) => 
            new Date(a.data).getTime() - new Date(b.data).getTime()
          )
          
          const now = new Date()
          const proxima = minhasPartidas
            .filter(p => new Date(p.data) > now && p.status !== 'CANCELADA')
            .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime())[0]
          
          return {
            categoriaTorneioId: c.id,
            torneioId: c.torneio?.id || '',
            torneio: {
              nome: c.torneio?.nome || '',
              avatarUrl: c.torneio?.avatarUrl || null,
              dataInicio: c.torneio?.dataInicio || null,
              dataFim: c.torneio?.dataFim || null,
            },
            categoria: {
              nome: c.nome,
              numeroAtletas: c.numeroAtletas ?? 1,
            },
            equipesInscritas: equipesCounts[idx] ?? 0,
            partidasAtletaCount: minhasPartidas.length,
            proximaFaseNome: proxima?.faseCategoriaTorneio?.nome || '',
            partidas: partidasOrdenadas,
          }
        })
        setCategoriasRows(rows)
      } catch (e) {
        setError('Erro ao carregar partidas.')
        setErrorCategorias('Erro ao carregar categorias de torneio.')
      } finally {
        setLoading(false)
        setLoadingCategorias(false)
      }
    })()
  }, [id])

  const handleVerInscricao = async (categoriaTorneioId: string) => {
    if (!id) return
    // setSelectedMatchId(match.id)
    setLoadingInscricao(true)
    setErrorInscricao('')
    try {
      const pagamentos = await listarPagamentosPorCategoriaTorneio(
        categoriaTorneioId,
        id,
      )
      if (pagamentos && pagamentos.length > 0) {
        navigate(`/athlete/tournamentinscriptions/${pagamentos[0].id}`)
      } else {
        setErrorInscricao('Nenhum pagamento encontrado para essa inscrição.')
      }
    } catch (e) {
      setErrorInscricao('Erro ao buscar inscrição.')
    } finally {
      setLoadingInscricao(false)
      setSelectedMatchId(null)
    }
  }

  const handleNavigateToCategory = (torneioId: string, categoriaId: string) => {
    if (!torneioId || !categoriaId) {
      console.error('torneioId ou categoriaId não disponível')
      return
    }
    navigate(`/liga/tournament/${torneioId}/category/${categoriaId}/manage?tab=2`)
  }

  const handleShowTrilhaPorCategoria = (categoriaTorneioId: string, torneioId?: string, categoriaNome?: string, torneioAvatarUrl?: string | null) => {
    if (!id) return
    const partidasDaCategoria = matches.filter(m => m.categoriaTorneio.id === categoriaTorneioId)
    let partnerInfo: BasicAtletaInfo | null = null
    const first = partidasDaCategoria[0]
    if (first) {
      // Lado do atleta (equipe ou participante avulso — rodízio). Lê a lista bruta do
      // lado para preservar o username (o util normaliza para id/nome/imagemUrl).
      const inA = atletaEstaNoLado(first, 'A', id)
      const listaLado = inA
        ? (first.equipeA?.atletas?.length ? first.equipeA.atletas : first.participantesA ?? [])
        : (first.equipeB?.atletas?.length ? first.equipeB.atletas : first.participantesB ?? [])
      if (first.categoriaTorneio?.numeroAtletas === 2 && listaLado.length > 0) {
        const p = listaLado.find(a => a.id !== id)
        if (p) {
          partnerInfo = {
            id: p.id,
            nome: p.usuario?.nome ?? 'Atleta',
            username: (p.usuario as any)?.username ?? null,
            imagemUrl: (p.usuario as any)?.imagemUrl ?? null,
          }
        }
      }
    }
    setPartner(partnerInfo)
    setTrailMatches(partidasDaCategoria)
    setTrailOpen(true)
    // Armazenar informações do torneio e categoria para o modal
    setSelectedCategoryInfo({
      torneioId: torneioId || '',
      categoriaId: categoriaTorneioId,
      categoriaNome: categoriaNome || '',
      torneioAvatarUrl: torneioAvatarUrl || null,
    })
  }



  const torneiosUnicos = useMemo(() => {
    const map = new Map<string, string>()
    for (const row of categoriasRows) {
      if (row.torneioId && row.torneio.nome) {
        map.set(row.torneioId, row.torneio.nome)
      }
    }
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome }))
  }, [categoriasRows])

  const statusOptions = [
    { value: 'AGENDADA', label: 'Agendada' },
    { value: 'EM_ANDAMENTO', label: 'Em andamento' },
    { value: 'FINALIZADA', label: 'Finalizada' },
    { value: 'CANCELADA', label: 'Cancelada' },
  ]

  const hasDateError = !!(filtroDataInicio && filtroDataFim && filtroDataInicio > filtroDataFim)

  const handleLimparFiltros = () => {
    setFiltroTorneioId('')
    setFiltroStatus('')
    setFiltroDataInicio('')
    setFiltroDataFim('')
  }

  const temFiltroAtivo = !!(filtroTorneioId || filtroStatus || filtroDataInicio || filtroDataFim)

  const sortedRows = useMemo(() => {
    let filtered = [...categoriasRows]

    if (filtroTorneioId) {
      filtered = filtered.filter(row => row.torneioId === filtroTorneioId)
    }

    if (filtroStatus) {
      filtered = filtered.filter(row =>
        row.partidas?.some(p => p.status === filtroStatus)
      )
    }

    if (filtroDataInicio && !hasDateError) {
      const inicio = new Date(filtroDataInicio + 'T00:00:00')
      filtered = filtered.filter(row => {
        if (!row.torneio.dataInicio) return false
        return new Date(row.torneio.dataInicio) >= inicio
      })
    }

    if (filtroDataFim && !hasDateError) {
      const fim = new Date(filtroDataFim + 'T23:59:59')
      filtered = filtered.filter(row => {
        if (!row.torneio.dataInicio) return false
        return new Date(row.torneio.dataInicio) <= fim
      })
    }

    return filtered.sort((a, b) => {
      const da = a.torneio.dataInicio ? new Date(a.torneio.dataInicio).getTime() : 0
      const db = b.torneio.dataInicio ? new Date(b.torneio.dataInicio).getTime() : 0
      return db - da
    })
  }, [categoriasRows, filtroTorneioId, filtroStatus, filtroDataInicio, filtroDataFim, hasDateError])

  return (
    <Fragment>
            <Header />

            <div className="main-content right-chat-active">
              <div className="container mt-4">
                <div className='flex column align-items-center'>
                  <button className="btn btn-link p-0" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left fs-1" />
                  </button>
                  <h4 className="d-inline-block ms-2 fw-700 text-primary">Meus jogos</h4>

                </div>
                {/* Filtros */}
                {!loading && !loadingCategorias && categoriasRows.length > 0 && (
                  <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap={2} mt={2} mb={1}>
                    <FormControl size="small" sx={{ flex: '1 1 200px', minWidth: 180 }}>
                      <InputLabel id="filtro-torneio-label">Torneio</InputLabel>
                      <Select
                        labelId="filtro-torneio-label"
                        value={filtroTorneioId}
                        label="Torneio"
                        onChange={(e: SelectChangeEvent) => setFiltroTorneioId(e.target.value)}
                      >
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {torneiosUnicos.map(t => (
                          <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ flex: '1 1 160px', minWidth: 150 }}>
                      <InputLabel id="filtro-status-label">Status</InputLabel>
                      <Select
                        labelId="filtro-status-label"
                        value={filtroStatus}
                        label="Status"
                        onChange={(e: SelectChangeEvent) => setFiltroStatus(e.target.value)}
                      >
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {statusOptions.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Data início"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={filtroDataInicio}
                      onChange={(e) => setFiltroDataInicio(e.target.value)}
                      error={hasDateError}
                      helperText={hasDateError ? 'Data início maior que data fim' : ' '}
                      sx={{ flex: '1 1 160px', minWidth: 150 }}
                    />

                    <TextField
                      label="Data fim"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={filtroDataFim}
                      onChange={(e) => setFiltroDataFim(e.target.value)}
                      error={hasDateError}
                      helperText=" "
                      sx={{ flex: '1 1 160px', minWidth: 150 }}
                    />

                    {temFiltroAtivo && (
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={handleLimparFiltros}
                        sx={{ alignSelf: 'center' }}
                      >
                        Limpar
                      </Button>
                    )}
                  </Box>
                )}

                {(loading || loadingCategorias) && <Load />}
                {(error || errorCategorias) && <div className="alert alert-danger mt-3">{error || errorCategorias}</div>}
                {errorInscricao && (
                  <div className="alert alert-danger mt-3">{errorInscricao}</div>
                )}
                {!loading && !loadingCategorias && !error && !errorCategorias && sortedRows.length === 0 && (
                  <p className="mt-3">
                    {temFiltroAtivo
                      ? 'Nenhuma categoria encontrada com os filtros selecionados.'
                      : 'Nenhuma categoria encontrada para este atleta.'}
                  </p>
                )}
                {!loading && !loadingCategorias && !error && !errorCategorias && (
                  <div className="mt-3" style={{ border: '2px solid #919191', borderRadius: '8px', overflow: 'hidden' }}>
                    
                    <Typography
                      sx={{
                        backgroundColor: '#919191',
                        color: 'white',
                        padding: 1,
                        paddingLeft: 4,
                        fontWeight: 'bold',
                      }}
                    >
                      Categorias de torneios
                    </Typography>
                    {sortedRows.map(row => {
                      return (
                        <PartidaItem
                          key={row.categoriaTorneioId}
                          atletaId={id ?? ''}
                          categoriaResumo={row}
                          onVerInscricao={() => handleVerInscricao(row.categoriaTorneioId)}
                          verInscricaoLoading={loadingInscricao}
                          onShowTrilha={() => handleShowTrilhaPorCategoria(row.categoriaTorneioId, row.torneioId, row.categoria.nome, row.torneio.avatarUrl)}
                          onNavigateToCategory={() => handleNavigateToCategory(row.torneioId, row.categoriaTorneioId)}
                        />
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
            <AtletaMatchesModal
              open={trailOpen}
              atleta={atletaInfo}
              parceiro={partner}
              partidas={trailMatches}
              onClose={() => setTrailOpen(false)}
              torneioId={selectedCategoryInfo.torneioId}
              categoriaId={selectedCategoryInfo.categoriaId}
              categoriaNome={selectedCategoryInfo.categoriaNome}
              torneioAvatarUrl={selectedCategoryInfo.torneioAvatarUrl}
              onNavigateToCategory={() => handleNavigateToCategory(selectedCategoryInfo.torneioId, selectedCategoryInfo.categoriaId)}
            />
    </Fragment>
  )
}

export default AthleteMatchesPage
